import React, { useEffect, useState } from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Textarea,
  Chip,
} from '@nextui-org/react';
import { Edit3, Plus } from 'lucide-react';
import forumService, {
  ForumTopic,
  extractForumError,
} from '../../services/forumService';

const TEAL = '#3FCED2';

interface EditTopicModalProps {
  isOpen: boolean;
  onClose: () => void;
  topic: ForumTopic;
  onUpdated: (topic: ForumTopic) => void;
}

/**
 * Topic-Bearbeiten-Modal (Port von forum_edit_topic_modal.dart).
 * Titel, Inhalt und Tags können angepasst werden.
 */
const EditTopicModal: React.FC<EditTopicModalProps> = ({
  isOpen,
  onClose,
  topic,
  onUpdated,
}) => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setTitle(topic.title || '');
      // Flutter speichert Delta-JSON – defensiv in Text umwandeln
      setContent(forumService.extractPlainText(topic.content));
      setTags(Array.isArray(topic.tags) ? [...topic.tags] : []);
      setTagInput('');
      setError(null);
    }
  }, [isOpen, topic]);

  const addTag = (raw: string) => {
    const trimmed = raw.trim().toLowerCase();
    if (trimmed.length > 0 && !tags.includes(trimmed) && tags.length < 5) {
      setTags((prev) => [...prev, trimmed]);
    }
    setTagInput('');
  };

  const handleSubmit = async () => {
    setError(null);
    const trimmedTitle = title.trim();
    if (trimmedTitle.length < 5) {
      setError('Der Titel muss mindestens 5 Zeichen haben.');
      return;
    }
    if (trimmedTitle.length > 200) {
      setError('Der Titel darf maximal 200 Zeichen haben.');
      return;
    }
    const trimmedContent = content.trim();
    if (trimmedContent.length < 20) {
      setError('Der Inhalt muss mindestens 20 Zeichen haben.');
      return;
    }

    const topicId = topic.id || topic._id;
    if (!topicId) {
      setError('Topic-ID fehlt.');
      return;
    }

    setSubmitting(true);
    try {
      const updated = await forumService.updateTopic(topicId, {
        title: trimmedTitle,
        content: trimmedContent,
        tags,
      });
      onClose();
      onUpdated(updated);
    } catch (err) {
      setError(extractForumError(err, 'Topic konnte nicht gespeichert werden'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="2xl"
      scrollBehavior="inside"
      classNames={{
        base: 'bg-zinc-900 border border-zinc-800 text-white',
        closeButton: 'text-gray-400 hover:bg-zinc-800',
      }}
    >
      <ModalContent>
        <ModalHeader className="flex items-center gap-2">
          <Edit3 size={20} style={{ color: TEAL }} />
          Topic bearbeiten
        </ModalHeader>
        <ModalBody className="gap-4">
          <Input
            label="Titel"
            value={title}
            onValueChange={setTitle}
            maxLength={200}
            classNames={{
              inputWrapper: 'bg-zinc-800 data-[hover=true]:bg-zinc-800',
              input: 'text-white',
              label: 'text-gray-400',
            }}
          />
          <Textarea
            label="Inhalt"
            value={content}
            onValueChange={setContent}
            minRows={6}
            maxLength={50000}
            classNames={{
              inputWrapper: 'bg-zinc-800 data-[hover=true]:bg-zinc-800',
              input: 'text-white',
              label: 'text-gray-400',
            }}
          />
          <div>
            <Input
              label="Tags (optional)"
              placeholder="Tag eingeben und Enter drücken..."
              value={tagInput}
              onValueChange={setTagInput}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addTag(tagInput);
                }
              }}
              endContent={
                <button
                  type="button"
                  onClick={() => addTag(tagInput)}
                  className="text-gray-400 hover:text-white"
                  aria-label="Tag hinzufügen"
                >
                  <Plus size={18} />
                </button>
              }
              classNames={{
                inputWrapper: 'bg-zinc-800 data-[hover=true]:bg-zinc-800',
                input: 'text-white',
                label: 'text-gray-400',
              }}
            />
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {tags.map((tag) => (
                  <Chip
                    key={tag}
                    onClose={() => setTags((prev) => prev.filter((t) => t !== tag))}
                    variant="flat"
                    className="bg-zinc-800 text-gray-300"
                  >
                    #{tag}
                  </Chip>
                ))}
              </div>
            )}
          </div>

          {error && <p className="text-red-500 text-sm">{error}</p>}
        </ModalBody>
        <ModalFooter>
          <Button
            variant="flat"
            className="bg-zinc-800 text-gray-300"
            onPress={onClose}
            isDisabled={submitting}
          >
            Abbrechen
          </Button>
          <Button
            style={{ backgroundColor: TEAL, color: '#000' }}
            className="font-semibold"
            onPress={handleSubmit}
            isLoading={submitting}
          >
            Speichern
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default EditTopicModal;
